import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type TireHotelRehomePublicRecord, TireHotelRehomeStatus } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, ScreenBaseWrapper } from 'noddi-ui-web';
import {
  customerRoutes,
  formatCurrencyAmount,
  NoddiDateFormats,
  noddiFormatDate,
  OPEN_REHOME_STATUSES
} from 'noddi-util';
import { NavioLicensePlate, NavioTireSeasonChip } from 'noddi-web';
import { Link, useParams } from 'react-router-dom';
import { getCommonTranslations } from '../../utils/commonTranslations';

export const TireHotelRehomePublic = () => {
  const { slug } = useParams();

  const {
    data: rehome,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getTireHotelRehomePublic,
    input: { slug: slug! }
  });

  if (isPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  return <RehomePage key={slug} slug={slug!} rehome={rehome} />;
};

interface RehomePageProps {
  slug: string;
  rehome: TireHotelRehomePublicRecord;
}

const RehomePage = ({ slug, rehome }: RehomePageProps) => {
  const { mutate: approve, isPending: isApproving } = noddiAsync.usePost({
    type: URLKeys.postTireHotelRehomePublicApprove,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getTireHotelRehomePublic,
          input: { slug }
        });
      }
    }
  });

  const { mutate: reject, isPending: isRejecting } = noddiAsync.usePost({
    type: URLKeys.postTireHotelRehomePublicReject,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getTireHotelRehomePublic,
          input: { slug }
        });
      }
    }
  });

  const isBusy = isApproving || isRejecting;
  const canDecide = OPEN_REHOME_STATUSES.includes(rehome.status);
  const sellerName = rehome.sellerName;
  const amountLabel = rehome.amount != null ? formatCurrencyAmount({ money: rehome.amount }) : null;
  const periodLabel =
    rehome.periodStartsAt != null && rehome.periodEndsAt != null
      ? `${noddiFormatDate(rehome.periodStartsAt, NoddiDateFormats.DATE)} – ${noddiFormatDate(
          rehome.periodEndsAt,
          NoddiDateFormats.DATE
        )}`
      : null;
  const expiresLabel = rehome.expiresAt != null ? noddiFormatDate(rehome.expiresAt, NoddiDateFormats.DATE_TIME) : null;

  return (
    <ScreenBaseWrapper width='md'>
      <div className='flex flex-col gap-4'>
        <header className='flex flex-col gap-1'>
          <h1 className='text-2xl font-bold text-primary sm:text-3xl md:text-4xl'>
            <Trans>Take over this tire hotel</Trans>
          </h1>
          <p className='text-sm'>
            <Trans>
              {sellerName} is rehoming the stored wheel set for this car to you. Approve the rehome and pay to start
              your tire hotel.
            </Trans>
          </p>
        </header>

        <NoddiBasicCard>
          <div className='flex flex-col gap-3'>
            <div className='flex flex-wrap items-center gap-2'>
              {rehome.licensePlateNumber ? (
                <NavioLicensePlate car={{ licensePlateNumber: rehome.licensePlateNumber }} />
              ) : null}
              {rehome.inStockSeason ? (
                <NavioTireSeasonChip season={rehome.inStockSeason} translations={getCommonTranslations().seasonBadge} />
              ) : null}
            </div>
            <dl className='grid grid-cols-1 gap-2 text-sm sm:grid-cols-2'>
              <div>
                <dt className='text-muted-foreground'>
                  <Trans>From</Trans>
                </dt>
                <dd className='font-medium'>{rehome.sellerName}</dd>
              </div>
              <div>
                <dt className='text-muted-foreground'>
                  <Trans>To</Trans>
                </dt>
                <dd className='font-medium'>{rehome.buyerName}</dd>
              </div>
              {amountLabel ? (
                <div>
                  <dt className='text-muted-foreground'>
                    <Trans>Amount due</Trans>
                  </dt>
                  <dd className='font-medium'>{amountLabel}</dd>
                </div>
              ) : null}
              {periodLabel ? (
                <div>
                  <dt className='text-muted-foreground'>
                    <Trans>Tire hotel period</Trans>
                  </dt>
                  <dd className='font-medium'>{periodLabel}</dd>
                </div>
              ) : null}
              {expiresLabel ? (
                <div>
                  <dt className='text-muted-foreground'>
                    <Trans>Expires</Trans>
                  </dt>
                  <dd className='font-medium'>{expiresLabel}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        </NoddiBasicCard>

        <StatusBanner status={rehome.status} />

        {canDecide && (
          <div className='flex flex-col gap-2 sm:flex-row'>
            <NoddiButton className='flex-1' disabled={isBusy} loading={isApproving} onClick={() => approve({ slug })}>
              <Trans>Approve rehome</Trans>
            </NoddiButton>
            <NoddiButton
              className='flex-1'
              variant='outline'
              disabled={isBusy}
              loading={isRejecting}
              onClick={() => reject({ slug })}
            >
              <Trans>Decline</Trans>
            </NoddiButton>
          </div>
        )}

        {canDecide && rehome.buyerBookingSlug ? (
          <NoddiButton asChild variant={canDecide ? 'outline' : 'primary'}>
            <Link to={customerRoutes.bookingInfo.getPath({ slug: rehome.buyerBookingSlug })}>
              {amountLabel ? t`Continue to payment (${amountLabel})` : t`Continue to payment`}
            </Link>
          </NoddiButton>
        ) : null}
      </div>
    </ScreenBaseWrapper>
  );
};

const StatusBanner = ({ status }: { status: TireHotelRehomeStatus }) => {
  if (status === TireHotelRehomeStatus.COMPLETED) {
    return (
      <NoddiBasicCard>
        <p>
          <Trans>This rehome is complete. The stored wheel set is now on your tire hotel.</Trans>
        </p>
      </NoddiBasicCard>
    );
  }
  if (status === TireHotelRehomeStatus.REJECTED) {
    return (
      <NoddiBasicCard>
        <p>
          <Trans>You declined this rehome. Contact the workshop if that was a mistake.</Trans>
        </p>
      </NoddiBasicCard>
    );
  }
  if (status === TireHotelRehomeStatus.CANCELLED) {
    return (
      <NoddiBasicCard>
        <p>
          <Trans>This rehome was cancelled by the workshop.</Trans>
        </p>
      </NoddiBasicCard>
    );
  }
  if (status === TireHotelRehomeStatus.EXPIRED) {
    return (
      <NoddiBasicCard>
        <p>
          <Trans>This rehome expired before it was approved and paid.</Trans>
        </p>
      </NoddiBasicCard>
    );
  }
  return null;
};
